import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "../services/api";
import { logout, getUser } from "../services/authService";
import { isPunchHiddenForEmployee } from "../lib/punchAccess";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Calendar, Bell, CircleDollarSign, User } from "lucide-react";
import { getAllApplicationDraftLines } from "../lib/applicationDrafts";
import './EmployeeCard.css';
import { AuthImage } from "../lib/authFile";

/** 数字 (⓪-⑩) */
const numberToCircle = (num) => {
  const n = parseInt(num, 10);
  if (Number.isNaN(n)) return num;
  const CIRCLE_NUMBER_BASE = 9311;
  if (n === 0) return '⓪';
  if (n >= 1 && n <= 10) return String.fromCodePoint(CIRCLE_NUMBER_BASE + n);
  return String(n);
};

/** 当日の HH:mm をローカル Date に（早退・残業判定用） */
const parseTodayHm = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(String(hhmm).trim())) return null;
  const [h, m] = String(hhmm).trim().split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

const formatNowHm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** ISO日時文字列を HH:mm（ローカル）に整形。失敗時は現在時刻。 */
const hmFromIso = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return formatNowHm();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** "YYYY-MM-DD" を「M月D日（曜）」に整形。失敗時はそのまま返す。 */
const fmtPunchDate = (ymd) => {
  if (!ymd) return "";
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return ymd;
  const DOW = ["日", "月", "火", "水", "木", "金", "土"];
  return `${d.getMonth() + 1}月${d.getDate()}日（${DOW[d.getDay()]}）`;
};

/** 始業定時（schedule_start）を過ぎているか */
const isLateForClockIn = (scheduleStart) => {
  const sched = parseTodayHm(scheduleStart);
  if (!sched) return false;
  return Date.now() > sched.getTime();
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/** 本日の遅刻申請が既にあるか（却下・取り下げ以外） */
const hasLateApplicationToday = async () => {
  try {
    const res = await api.get("/api/employee/punch-applications/");
    const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
    const today = todayIso();
    return list.some((item) => {
      if (item.category !== "LATE_IN") return false;
      if (item.status === "REJECTED" || item.status === "CANCELLED") return false;
      const fdDate = item.late_date || item.date || "";
      const submittedDay = String(item.submittedAt || "").slice(0, 10);
      return fdDate === today || submittedDay === today;
    });
  } catch {
    return false;
  }
};

const API = {
  LEAVE: (memberId) => `/api/employees/${memberId}/leave/`,
  NOTIFICATIONS_UNREAD: '/api/notifications/',
  NOTIFICATION_MARK_READ: (id) => `/api/notifications/${id}/mark-read/`,
  PUNCH_REMINDER_CHECK: '/api/worktime/my/punch-reminder/',
};

/** 上司向け部下申請通知（タイトルに申請名・日付を含む） */
const SUBORDINATE_APPLICATION_CONFIRM_TITLE_PREFIX = "【部下申請の確認依頼】";
/** スケジュール未登録時に管理側一覧取得で自動送信されるお知らせ */
const SCHEDULE_REMINDER_TITLE = "スケジュール登録のお願い";
const ROUTES = { WORKTIME: '/worktime', APPLICATIONS: '/applications', LOGIN: '/m/login' };
// 残業休憩時間の選択肢（時間単位 0〜6.0 を 0.25 刻み）。残業申請フォームと揃える。内部は分で保持する。
const OVERTIME_BREAK_HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i * 0.25);
const PUNCH_REMINDER_TITLE = '打刻のお願い';
const EVENT_NOTICE_CATEGORY = 'event';
const APPLICATION_NOTICE_TITLES_FROM_ADMIN = new Set([
  '【申請承認のお知らせ】',
  '【申請却下のお知らせ】',
  '【申請差し戻しのお知らせ】',
  '【上司承認のお知らせ】残業の本申請をお願いします',
  '【上司却下のお知らせ】',
]);

/**
 * 管理側のお知らせ作成画面 ≠ system-auto-generated:
 * category="company" でもシステムが自動送信するタイトルは除外する。
 * バックエンドの NOTICE_PERSONAL_TITLES と対応。
 */
const SYSTEM_AUTO_NOTICE_TITLES = new Set([
  '申請を受け付けました',
  '事前申請を受け付けました',
  '出勤を記録しました',
  '退勤を記録しました',
  'イベントのお知らせ',
  SCHEDULE_REMINDER_TITLE,
]);

const EmployeeCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [companyInfo, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrPayload, setQrPayload] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [pendingAnketo, setPendingAnketo] = useState([]);
  const [pendingParticipation, setPendingParticipation] = useState([]);
  const [todayQuote, setTodayQuote] = useState("");
  // シフト勤務者は残業の対象外（残業確認ダイアログ・残業申請を出さない）
  const [isShiftWorker, setIsShiftWorker] = useState(false);
  // 残業として退勤する際に、残業終了時刻・休憩・（予定とズレた場合の）理由を入力するダイアログ
  const [overtimeDialog, setOvertimeDialog] = useState(null); // { plannedEnd } | null
  const [otFinishedAt, setOtFinishedAt] = useState("");
  const [otBreakMinutes, setOtBreakMinutes] = useState(0);
  const [otReason, setOtReason] = useState("");
  // 打刻状態
  const [status, setStatus] = useState({
    working: false,
    has_schedule_today: false,
    can_clock_in: false,
    has_yesterday_punch_missing: false,
    has_prev_workday_no_punch: false,
    prev_workday_no_punch_date: null,
    clock_in_at: null,
    schedule_start: null,
    schedule_end: null,
  });
  /** clock_in_at があるときのみ退社は出勤30分後から */
  const [canShowClockOut, setCanShowClockOut] = useState(false);

  // --- States ---
  const [unreadCount, setUnreadCount] = useState(0);
  const [remainingLeave, setRemainingLeave] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(true);
  const [useEmployeeSchedule, setUseEmployeeSchedule] = useState(false);
  
  const [punchReminderCount, setPunchReminderCount] = useState(0);
  const [punchReminderNotices, setPunchReminderNotices] = useState([]);
  const [showPunchReminderModal, setShowPunchReminderModal] = useState(false);

  const [showNoticeDeadlineModal, setShowNoticeDeadlineModal] = useState(false);
  const [selectedNoticeDeadline, setSelectedNoticeDeadline] = useState(null);
  const [appNoticeCount, setAppNoticeCount] = useState(0);
  const [applicationNotices, setApplicationNotices] = useState([]);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  const [eventNoticeCount, setEventNoticeCount] = useState(0);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEventNotice, setSelectedEventNotice] = useState(null);
  const [anketoNotices, setAnketoNotices] = useState([]);
  const [showAnketoModal, setShowAnketoModal] = useState(false);


  /** 上司向け: 部下の残業申請（承認待ち） */
  const [subordinateOvertimeList, setSubordinateOvertimeList] = useState([]);
  /** 上司向け: 部下の打刻忘れ件数・上司判定 */
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [forgotPunchCount, setForgotPunchCount] = useState(0);

  const [applicationDraftTick, setApplicationDraftTick] = useState(0);
  const applicationDraftLines = useMemo(
    () => getAllApplicationDraftLines(),
    [applicationDraftTick]
  );
  const showDraftInStar = useMemo(() => {
    const pos = String(employee?.position || "");
    // 課長・部長などの上位者の社員証では「申請一時保存」を表示しない
    return !pos.includes("課長") && !pos.includes("部長");
  }, [employee?.position]);

  /**
   * お知らせバッジ用: 管理側のお知らせ作成画面で作成した通知のみ表示。
   * category="company" かつ システム自動送信タイトルを除外。
   * 社員側のお知らせ画面で既読にすると自動で数が減る（visibilitychange で再取得）。
   */
  const displayNotifications = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    return list.filter((n) => {
      // 管理側のお知らせ作成からのみ（category="company"）
      if (n.category !== "company") return false;
      // システム自動送信タイトルは除外
      if (SYSTEM_AUTO_NOTICE_TITLES.has(n.title)) return false;
      // アンケート系を除外
      if (n.category === "event_anketo_request" || String(n.title || "").startsWith("★アンケート回答のお願い")) return false;
      return true;
    });
  }, [notifications]);

  /** お知らせバッジ用: 管理側のお知らせ作成からの通知のうち、確認締切に達している未読通知 */
  const deadlineReachedNotices = useMemo(() => {
    const list = Array.isArray(displayNotifications) ? displayNotifications : [];
    
    const toYmd = (d) => {
      const x = new Date(d);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    };
    const todayStr = toYmd(new Date());

    return list.filter((n) => {
      if (!n.read_deadline_date) return false;
      const deadlineStr = toYmd(n.read_deadline_date);
      // 今日が確認締切日に当たるか過ぎている、かつ未読の通知を抽出
      return todayStr >= deadlineStr;
    });
  }, [displayNotifications]);

  // 中止になったイベントのお知らせ（社員証にイベントバッジで表示する）
  const eventCancelNotices = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    return list.filter((n) => n.category === "event_cancelled");
  }, [notifications]);

  const current = getUser?.();
  const username = current?.username || current?.sub || null;
  
  useEffect(() => {
    const onDraft = () => setApplicationDraftTick((t) => t + 1);
    window.addEventListener("application-draft-saved", onDraft);
    return () => window.removeEventListener("application-draft-saved", onDraft);
  }, []);

  const refreshStarArea = useCallback(async () => {
    try {
      const notiRes = await api.get("/api/notifications/");
      setNotifications(notiRes.data.results ?? notiRes.data ?? []);
    } catch (e) {
      console.error(e);
    }
    let ank = [];
    let part = [];
    try {
      const ankRes = await api.get("/api/events/pending_anketo/");
      ank = Array.isArray(ankRes.data) ? ankRes.data : [];
    } catch (e) {
      if (e.response?.status !== 404) console.error(e);
    }
    try {
      const partRes = await api.get("/api/events/pending_participation/");
      part = Array.isArray(partRes.data) ? partRes.data : [];
    } catch (e) {
      if (e.response?.status !== 404) console.error(e);
    }
    setPendingAnketo(ank);
    setPendingParticipation(part);
  }, []);

  //  回答締切 (regDeadline) が近いイベントで、参加可否が未回答のものを抽出
  const autoEventReminders = useMemo(() => {
    // 未回答（yes/no以外）かつ回答締切が近いイベントを抽出
    const list = Array.isArray(pendingParticipation) ? pendingParticipation : [];
    
    //  YYYY/MM/DD 形式の今日の日付を作成
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const todayStr = `${y}/${m}/${d}`;

    const formatDateStr = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };

    return list.filter((event) => {
      if (event.is_cancelled) return false;
      if (!event.regDeadline) return false;
      
      const deadlineStr = formatDateStr(event.regDeadline);
      // 回答締切に今日がちょうど当たるか過ぎている、かつ回答未済のイベントを抽出
      return deadlineStr && todayStr >= deadlineStr;
    });
  }, [pendingParticipation]);

  useEffect(() => {
    refreshStarArea();
  }, [refreshStarArea]);

  // 本日の「今日の一言」を取得（無ければ空文字で非表示）
  useEffect(() => {
    let alive = true;
    api.get("/api/quotes/today/")
      .then((res) => { if (alive) setTodayQuote(res.data?.content || ""); })
      .catch(() => { if (alive) setTodayQuote(""); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") refreshStarArea();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refreshStarArea]);

  
  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/mark-read/`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      console.error("Failed to mark read:", e);
    }
  };


  // --- Fetch データベース ---
  const fetchData = useCallback(async () => {
    try {
      const [empRes, compRes, setRes] = await Promise.all([
        api.get("/api/employee/me/"),
        api.get("/api/company-info/"),
        api.get("/api/employee-card/display-setting/")
      ]);
      const emp = empRes.data?.employee || empRes.data;
      setEmployee(emp);
      setIsShiftWorker(emp?.work_type === "SHIFT");
      const company = compRes.data || {};
      setCompany(company);
      setSettings(setRes.data);
      setUseEmployeeSchedule(Boolean(compRes.data?.use_employee_schedule));
    } catch (err) {
      console.error("データ取得エラー:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  

  // 打刻状態をAPIから取得して更新する関数（出社・退社後、または画面再表示時に呼び出す）
  const fetchWorkStatus = useCallback(async () => {
    try {
      const res = await api.get("/api/worktime/status/");
      setStatus(res.data);
    } catch (err) {
      console.error("ステータス取得失敗", err);
    }
  }, []);

  useEffect(() => {
    fetchWorkStatus();
    // スケジュール開始 1 時間前で「出社」ボタンが自動表示されるよう、定期再取得
    const id = setInterval(fetchWorkStatus, 30 * 1000);
    return () => clearInterval(id);
  }, [fetchWorkStatus]);

  /** 出勤直後30分は退社ボタン無効（API が clock_in_at を返すときのみ） */
  useEffect(() => {
    if (!status.working) {
      setCanShowClockOut(true);
      return;
    }
    if (!status.clock_in_at) {
      setCanShowClockOut(true);
      return;
    }
    const startMs = new Date(status.clock_in_at).getTime();
    if (Number.isNaN(startMs)) {
      setCanShowClockOut(true);
      return;
    }
    const deadline = startMs + 30 * 60 * 1000;
    const tick = () => setCanShowClockOut(Date.now() >= deadline);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status.working, status.clock_in_at]);

  {/*}
  const finalizeClockOut = async (body = {}) => {
    setLoading(true);
    try {
      const res = await api.post("/api/worktime/clock_out/", body);
      alert(`退社を登録しました。退社時刻：${hmFromIso(res.data?.end_time)}`);
      await fetchWorkStatus();
      fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.message || "打刻に失敗しました。");
    } finally {
      setLoading(false);
    }
  };
  */}

  /** 残業として退勤する際の入力ダイアログを開く。既定の残業終了時刻は現在時刻。 */
  const openOvertimeDialog = (plannedEnd) => {
    const now = new Date();
    const hm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    setOtFinishedAt(hm);
    setOtBreakMinutes(0);
    setOtReason("");
    setOvertimeDialog({ plannedEnd: plannedEnd || "" });
  };

  /** ダイアログの入力内容で退勤する（＝残業の本申請まで確定させる） */
  const submitOvertimeClockOut = async () => {
    const planned = overtimeDialog?.plannedEnd || "";
    if (!otFinishedAt) {
      alert("残業が終わった時刻を入力してください。");
      return;
    }
    // 未来の時刻は入れられない（残業終了より後に退勤するため）
    const nowD = new Date();
    const nowHm = `${String(nowD.getHours()).padStart(2, "0")}:${String(nowD.getMinutes()).padStart(2, "0")}`;
    if (otFinishedAt > nowHm) {
      alert("残業終了時刻に未来の時刻は入力できません。");
      return;
    }
    // 予定とズレている場合は理由が必須（超過理由 / 早く終了理由）
    if (planned && otFinishedAt !== planned && !otReason.trim()) {
      alert(
        otFinishedAt > planned
          ? "残業終了予定を超えています。超過理由を入力してください。"
          : "残業終了予定より早く終わっています。早く終了した理由を入力してください。"
      );
      return;
    }
    setOvertimeDialog(null);
    await finalizeClockOut({
      is_overtime: true,
      overtime_finished_at: otFinishedAt,
      overtime_break_minutes: otBreakMinutes,
      overtime_deviation_reason: otReason.trim(),
    });
  };

  /** 本日分の上司事前承認（残業）の有無を取得。
   *  返り値: { approvedToday, pendingToday }（いずれも該当があれば申請オブジェクト、なければ null） */
  const fetchOvertimeSupervisorStateToday = async () => {
    try {
      const res = await api.get("/api/employee/my-overtime/supervisor-pending/");
      const todayStr = new Date().toISOString().slice(0, 10);
      const approved = (res.data?.approved || []).find((a) => (a.date || "") === todayStr) || null;
      const pending = (res.data?.pending || []).find((p) => (p.date || "") === todayStr) || null;
      return { approvedToday: approved, pendingToday: pending };
    } catch (_) {
      return { approvedToday: null, pendingToday: null };
    }
  };
  {/*}
  const handleClockOutClick = async () => {
    if (status.has_yesterday_punch_missing) {
      const ok = window.confirm(
        "昨日以前の退社打刻が漏れています。実績修正画面で正しく入力してから本日の出社を行ってください。"
      );
      if (ok) navigate("/worktime/record");
      return;
    }
    const now = new Date();
    const endHm = status.schedule_end;
    if (endHm) {
      const endD = parseTodayHm(endHm);
      if (endD && now.getTime() < endD.getTime() - 60 * 1000) {
        // スケジュール終了時刻より前に退勤する場合 → 早退確認
        if (window.confirm("早退でよろしいでしょうか？")) {
          // 早退申請画面側で申請成功時に退勤打刻が自動実行されるため、ここでは画面遷移のみ
          navigate("/selected-forms?ids=timeOver&punchCategory=EARLY_OUT");
        }
        // キャンセル時は何もせず社員証画面に留まる
        return;
      }
      if (endD && now.getTime() > endD.getTime() + 60 * 1000) {
        // 残業時間帯（シフト終了 +1 分以降）の退勤分岐
        const requireSupervisor = Boolean(companyInfo?.overtime_requires_supervisor_approval);

        if (requireSupervisor) {
          // 【ワークフロー ON】上司の事前承認がある場合のみ「残業として退勤」可
          const { approvedToday, pendingToday } = await fetchOvertimeSupervisorStateToday();
          if (approvedToday) {
            if (
              window.confirm(
                "本日の残業は上司に承認されています。残業として退勤しますか？"
              )
            ) {
              await finalizeClockOut({ is_overtime: true });
            }
            return;
          }
          if (pendingToday) {
            alert(
              "残業の事前申請が上司の承認待ちです。\n承認後に退勤を行ってください。"
            );
            return;
          }
          // 未申請または却下 → 私用で退勤するか、事前申請するかを確認
          const choice = window.confirm(
            "本日の残業について上司の事前承認がありません。\n" +
              "「OK」: 私用として退勤します（残業申請はしません）\n" +
              "「キャンセル」: 残業の事前申請を行います"
          );
          if (choice) {
            await finalizeClockOut();
          } else {
            navigate("/selected-forms?ids=timeOver&punchCategory=OVERTIME");
          }
          return;
        }

        // 【ワークフロー OFF】はい=残業として退勤+申請画面 / いいえ=私用で退勤
        const wantOvertime = window.confirm(
          "残業しますか？\n" +
            "「OK」: 残業として退勤し、残業申請画面へ進みます\n" +
            "「キャンセル」: 私用として退勤します（残業申請はしません）"
        );
        if (wantOvertime) {
          await finalizeClockOut({ is_overtime: true });
          navigate("/selected-forms?ids=timeOver&punchCategory=OVERTIME");
        } else {
          await finalizeClockOut();
        }
        return;
      }
    }
    await finalizeClockOut();
  };
  */}
  {/*}
  const handleClockIn = async () => {
    if (status.has_yesterday_punch_missing) {
      const ok = window.confirm(
        "昨日以前の退社打刻が漏れています。実績修正画面で正しく入力してから本日の出社を行ってください。"
      );
      if (ok) navigate("/worktime/record");
      return;
    }

    if (status.schedule_start && isLateForClockIn(status.schedule_start)) {
      const alreadyApplied = await hasLateApplicationToday();
      if (!alreadyApplied) {
        const planned = formatNowHm();
        const go = window.confirm(
          `始業時刻（${status.schedule_start}）を過ぎています。\n` +
            "出社打刻を記録し、遅刻申請画面へ進みます。\n\n" +
            "「OK」: 出社打刻＋遅刻申請へ　「キャンセル」: 中止"
        );
        if (go) {
          setLoading(true);
          try {
            await api.post("/api/worktime/clock_in/");
            const res = await api.get("/api/worktime/status/");
            setStatus(res.data);
            fetchNotifications();
            const q = new URLSearchParams({
              punchCategory: "LATE_IN",
              scheduleStart: status.schedule_start,
              plannedStart: planned,
              fromPunch: "1",
            });
            navigate(`/worktime/application?${q.toString()}`);
          } catch (err) {
            alert(
              err.response?.data?.detail ||
                err.response?.data?.message ||
                "出社打刻に失敗しました。"
            );
          } finally {
            setLoading(false);
          }
        }
        return;
      }
    }

    setLoading(true);
    try {
      const inRes = await api.post("/api/worktime/clock_in/");
      alert(`出社を記録しました。記録時刻：${hmFromIso(inRes.data?.start_time)}。本日はよろしくお願いします。`);
      const res = await api.get("/api/worktime/status/");
      setStatus(res.data);
      fetchNotifications();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.message || "打刻に失敗しました。");
    } finally {
      setLoading(false);
    }
  };
  */}

  const finalizeClockOut = async (extraBody = {}, remoteReason = "") => {
    // 1. 端末がGPS位置情報（Geolocation）に対応しているか確認
    if (!navigator.geolocation) {
      alert("お使いの端末はGPS位置情報に対応していません。");
      return;
    }

    setLoading(true);

    // 2. 端末のGPSから現在の位置情報（緯度・経度）を取得
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // 🚀 バックエンドの退社APIへGPS座標、既存のデータ、および勤務理由を送信
          const res = await api.post("/api/worktime/clock_out/", {
            ...extraBody,
            latitude: latitude,
            longitude: longitude,
            remote_reason: remoteReason
          });

          // 打刻成功時、判定された勤務地名と距離の誤差を表示
          const wpName = res.data?.workplace_name ? `【${res.data.workplace_name}】` : "";
          const dist = res.data?.distance_m ? ` (距離誤差: ${parseInt(res.data.distance_m)}m)` : "";

          const otMsg = res.data?.overtime_finalized
            ? "\n残業を申請しました（管理者の承認待ち）。"
            : "";
          alert(`退社を記録しました。${wpName}${dist}\n記録時刻：${hmFromIso(res.data?.end_time)}。お疲れ様でした！${otMsg}`);

          await fetchWorkStatus();
          fetchNotifications();

          // 残業として退勤したが、上司承認済みの事前申請が無く本申請まで確定できなかった場合は
          // 残業申請画面へ進んでもらう（そうしないと残業がどこにも残らない）。
          if (extraBody.is_overtime && !res.data?.overtime_finalized) {
            navigate("/selected-forms?ids=timeOver&punchCategory=OVERTIME");
          }
        } catch (err) {
          // 🚨 エリア外打刻エラー：バックエンドから理由の入力を求められた場合
          if (err.response?.data?.require_reason) {
            const reason = window.prompt(`${err.response.data.message}`);
            if (reason !== null && reason.trim() !== "") {
              // 理由を保持したまま再試行（再帰呼び出し）
              await finalizeClockOut(extraBody, reason.trim());
            }
          } else {
            alert(err.response?.data?.detail || err.response?.data?.message || "打刻に失敗しました。");
          }
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        // GPS位置情報の取得に失敗した場合（権限拒否など）のハンドリング
        setLoading(false);
        alert("位置情報の取得に失敗しました。ブラウザの位置情報利用を許可してください。");
      },
      { enableHighAccuracy: true, timeout: 10000 } // 高精度なGPS測位を要求
    );
  };

  const handleClockOutClick = async () => {
    // 1. 前日以前の退社打刻漏れ（打刻忘れ）がないか確認
    if (status.has_yesterday_punch_missing) {
      const ok = window.confirm(
        "昨日以前の退社打刻が漏れています。実績修正画面で正しく入力してから本日の出社を行ってください。"
      );
      if (ok) navigate("/worktime/record");
      return;
    }

    const now = new Date();
    const endHm = status.schedule_end;

    if (endHm) {
      const endD = parseTodayHm(endHm);
      
      // --- ケースA：スケジュール終了時刻より前に退勤する場合（早退確認） ---
      if (endD && now.getTime() < endD.getTime() - 60 * 1000) {
        const wantEarlyOut = window.confirm("早退でよろしいでしょうか？");
        if (wantEarlyOut) {
          // 早退申請画面側で申請成功時に退勤打刻が自動実行されるため、画面遷移のみ行う。
          // 「退社予定」「退社時刻」は申請画面で readOnly なので、ここで値を渡して
          // 自動表示させる（遅刻申請で scheduleStart/plannedStart を渡すのと同じ扱い）。
          const q = new URLSearchParams({
            ids: "timeOver",
            punchCategory: "EARLY_OUT",
            scheduleEnd: endHm,
            plannedEnd: formatNowHm(),
          });
          navigate(`/selected-forms?${q.toString()}`);
          return;
        } else {
          const isWorkFinished = window.confirm("本日の業務は終了でよろしいですか？");
          if (isWorkFinished) {
            await finalizeClockOut();
          }
          return;
        }
        
      }

      // --- ケースB：残業時間帯（シフト終了 +1分以降）の退勤判定 ---
      // シフト勤務者は残業の対象外。退社予定を過ぎても残業確認は出さず、そのまま退勤する。
      if (endD && now.getTime() > endD.getTime() + 60 * 1000 && !isShiftWorker) {
        const requireSupervisor = Boolean(companyInfo?.overtime_requires_supervisor_approval);

        if (requireSupervisor) {
          // 【上司承認必須フロー ON】上司の事前承認がある場合のみ「残業として退勤」を許可
          const { approvedToday, pendingToday } = await fetchOvertimeSupervisorStateToday();
          
          if (approvedToday) {
            if (window.confirm("本日の残業は上司に承認されています。残業として退勤しますか？")) {
              // 残業終了時刻・休憩・理由を入力してもらい、その内容で本申請まで確定する
              openOvertimeDialog(approvedToday.plannedEnd || "");
            }
            return;
          }
          if (pendingToday) {
            alert("残業の事前申請が上司の承認待ちです。\n承認後に退勤を行ってください。");
            return;
          }
          
          // 未申請または却下時の確認処理
          const choice = window.confirm(
            "本日の残業について上司の事前承認がありません。\n" +
              "「OK」: 私用として退勤します（残業申請はしません）\n" +
              "「キャンセル」: 残業の事前申請を行います"
          );
          if (choice) {
            await finalizeClockOut();
          } else {
            navigate("/selected-forms?ids=timeOver&punchCategory=OVERTIME");
          }
          return;
        }

        // 【上司承認必須フロー OFF】残業として退勤するか、私用として退勤するかを選択
        const wantOvertime = window.confirm(
          "残業しますか？\n" +
            "「OK」: 残業として退勤します\n" +
            "「キャンセル」: 私用として退勤します（残業申請はしません）"
        );
        if (wantOvertime) {
          // 事前申請が無い運用のため、比較する予定時刻は無い（＝理由欄は出さない）
          const { approvedToday } = await fetchOvertimeSupervisorStateToday();
          openOvertimeDialog(approvedToday?.plannedEnd || "");
        } else {
          await finalizeClockOut();
        }
        return;
      }
    }

    // スケジュール設定がない、または時間内の通常退勤処理
    await finalizeClockOut();
  };

  // lateStartTime: 遅刻申請の送信後に呼ばれたとき、ボタンを押した時刻("HH:MM")で打刻する。
  // （申請入力にかかった時間の分だけ出社時刻が遅くならないようにするため）
  const handleClockIn = async (remoteReason = "", remoteChoice, lateStartTime = "") => {
    // 1. 前回の出勤予定日がまるごと未打刻（出社・退社とも押し忘れ）なら出社できない
    if (status.has_prev_workday_no_punch) {
      const ok = window.confirm(
        `前回の出勤予定日（${fmtPunchDate(status.prev_workday_no_punch_date)}）の出社・退社打刻が入力されていません。` +
          "実績画面で入力してから本日の出社を行ってください。"
      );
      if (ok) navigate("/worktime/record");
      return;
    }

    // 2. 昨日以前の退社打刻が漏れるかどうか確認
    if (status.has_yesterday_punch_missing) {
      const ok = window.confirm(
        "昨日以前の退社打刻が漏れています。実績修正画面で正しく入力してから本日の出社を行ってください。"
      );
      if (ok) navigate("/worktime/record");
      return;
    }

    // 在宅予定日（在宅シフト）の出社：まず「今日は在宅ですか？」を確認（方式1）
    if (status.is_remote_today && remoteChoice === undefined) {
      const yes = window.confirm(
        "今日は在宅ですか？\n\n「OK」＝在宅で打刻\n「キャンセル」＝会社（職場）で打刻"
      );
      remoteChoice = yes ? "yes" : "no";
    }

    // 在宅（はい）は GPS 判定・理由入力なしで直接打刻
    if (remoteChoice === "yes") {
      setLoading(true);
      try {
        const inRes = await api.post("/api/worktime/clock_in/", {
          remote_choice: "yes",
          ...(lateStartTime ? { actual_start_time: lateStartTime } : {}),
        });
        alert(`在宅で出社を記録しました。\n記録時刻：${hmFromIso(inRes.data?.start_time)}。本日はよろしくお願いします。`);
        const res = await api.get("/api/worktime/status/");
        setStatus(res.data);
        fetchNotifications();
      } catch (err) {
        alert(err.response?.data?.detail || err.response?.data?.message || "出社打刻に失敗しました。");
      } finally {
        setLoading(false);
      }
      return;
    }

    // 3. GPS位置情報に対応する確認
    if (!navigator.geolocation) {
      alert("お使いの端末はGPS位置情報に対応していません。");
      return;
    }

    setLoading(true);

    // 3. 実際のGPS位置情報に対応
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // --- 遅刻 ---
        if (status.schedule_start && isLateForClockIn(status.schedule_start)) {
          const alreadyApplied = await hasLateApplicationToday();
          if (!alreadyApplied) {
            const planned = formatNowHm();
            // 早退と同じ2段確認。始業時刻を過ぎていても、上司の指示で開始時刻が変わった等で
            // 遅刻ではない場合があるため、「遅刻ではない」を選べるようにする。
            const isLate = window.confirm(
              `始業時刻（${status.schedule_start}）を過ぎています。遅刻ですか？\n\n` +
                "「OK」: 遅刻（申請画面へ進みます）\n" +
                "「キャンセル」: 遅刻ではない"
            );
            if (!isLate) {
              const startNow = window.confirm(
                `業務開始ですか？\n\n「OK」: この時刻（${planned}）で出社打刻\n「キャンセル」: 中止`
              );
              if (!startNow) {
                setLoading(false);
                return;
              }
              // 遅刻ではないので申請せず、そのまま通常の出社打刻に進む（下の処理へ）
            } else {
              setLoading(false);
              // 打刻は遅刻申請の送信後に行う（申請しないまま出社打刻だけ残るのを防ぐ）。
              // 出社時刻はこのボタンを押した時刻(planned)を使う。
              const q = new URLSearchParams({
                punchCategory: "LATE_IN",
                scheduleStart: status.schedule_start,
                plannedStart: planned,
                fromPunch: "1",
                returnTo: "/card_front",
              });
              navigate(`/worktime/application?${q.toString()}`);
              return;
            }
          }
        }

        // --- 正しく時間 ---
        try {
          // 自宅業務の場合
          const inRes = await api.post("/api/worktime/clock_in/", {
            latitude,
            longitude,
            remote_reason: remoteReason,
            ...(remoteChoice ? { remote_choice: remoteChoice } : {}),
            ...(lateStartTime ? { actual_start_time: lateStartTime } : {}),
          });

          // 現場と距離誤差
          const wpName = inRes.data?.workplace_name ? `【${inRes.data.workplace_name}】` : "";
          const dist = inRes.data?.distance_m ? ` (距離誤差: ${parseInt(inRes.data.distance_m)}m)` : "";

          alert(`出社を記録しました。${wpName}${dist}\n記録時刻：${hmFromIso(inRes.data?.start_time)}。本日はよろしくお願いします。`);

          const res = await api.get("/api/worktime/status/");
          setStatus(res.data);
          fetchNotifications();
        } catch (err) {
          // 🚨 理由を登録 (正しく閉じカッコを整理)
          if (err.response?.data?.require_reason) {
            const reason = window.prompt(`${err.response.data.message}`);
            if (reason !== null && reason.trim() !== "") {
              handleClockIn(reason.trim(), remoteChoice, lateStartTime);
            }
          } else {
            alert(err.response?.data?.detail || err.response?.data?.message || "打刻に失敗しました。");
          }
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        // GPSの位置を設定しない
        setLoading(false);
        alert("位置情報の取得に失敗しました。ブラウザの位置情報利用を許可してください。");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handlePunch = async () => {
    if (status.working) {
      await handleClockOutClick();
    } else {
      await handleClockIn();
    }
  };

  // 遅刻申請を送信して戻ってきたら、そのまま出社打刻を行う。
  // 打刻時刻は申請画面へ進む前にボタンを押した時刻（latePunch）を使う。
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const latePunch = params.get("latePunch");
    if (!latePunch || status.working) return;
    // 同じ打刻を二重に実行しないよう、先にURLからパラメータを消す
    navigate("/card_front", { replace: true });
    handleClockIn("", undefined, latePunch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, status.working]);

  // 3. ボタンの表示ロジック
  const renderPunchButton = () => {
    // 社長など特定の役職では出社・退社（出退勤）ボタンを表示しない
    if (isPunchHiddenForEmployee(employee)) return null;
    // ボタン下に「予定時間：HH:MM（スケジュール）」を表示。未設定なら何も出さない。
    const scheduleNote = (time) =>
      time ? (
        <p className="punch-schedule-note" style={{ marginTop: 6, textAlign: "center", fontSize: "0.85rem", color: "#475569" }}>
          予定時間：<b>{time}</b>
        </p>
      ) : null;

    if (status.working) {
      const waitThirtyMin = Boolean(status.clock_in_at);
      // 出勤直後30分はボタン自体を表示しない（経過後に退社ボタンを表示）
      if (waitThirtyMin && !canShowClockOut) {
        return (
          <div className="punch-after-wait-wrap" style={{ marginTop: 12, textAlign: "center" }}>
            <p style={{ fontSize: "0.85rem", color: "#475569" }}>
              退社打刻は出勤から30分経過後に表示されます。
            </p>
          </div>
        );
      }
      return (
        <>
          <button type="button" onClick={handlePunch} className="btn-punch btn-red">
            退社
          </button>
          {scheduleNote(status.schedule_end)}
        </>
      );
    }

    // 前回の出勤予定日がまるごと未打刻 → 出社ボタンはグレーアウトし、理由と修正導線を出す
    if (status.has_prev_workday_no_punch) {
      return (
        <>
          <button type="button" className="btn-punch btn-green" disabled>
            出社
          </button>
          <p className="punch-blocked-note">
            前回の出勤予定日（{fmtPunchDate(status.prev_workday_no_punch_date)}）の出社・退社打刻が
            入力されていません。実績画面で入力してから出社してください。
          </p>
          <Link to="/worktime/record" className="punch-blocked-link">
            実績画面へ
          </Link>
        </>
      );
    }

    if (!useEmployeeSchedule) {
      return (
        <>
          <button type="button" onClick={handlePunch} className="btn-punch btn-green">
            出社
          </button>
          {scheduleNote(status.schedule_start)}
        </>
      );
    }

    if (!status.has_schedule_today) {
      return null;
    }

    if (status.can_clock_in) {
      return (
        <>
          <button type="button" onClick={handlePunch} className="btn-punch btn-green">
            出社
          </button>
          {scheduleNote(status.schedule_start)}
        </>
      );
    }

    return null;
  };

  // --- Fetch 通知 & バッジ  ---
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, punchRes] = await Promise.allSettled([
        api.get(API.NOTIFICATIONS_UNREAD, { params: { _t: Date.now() } }),
        api.get(API.PUNCH_REMINDER_CHECK),
      ]);

      // 通知一覧
      const list = notifRes.status === "fulfilled"
        ? (Array.isArray(notifRes.value.data) ? notifRes.value.data : (notifRes.value.data?.results || []))
        : [];

      const notifReminders = list.filter(n => String(n.title) === PUNCH_REMINDER_TITLE || (String(n.category).toLowerCase() === "personal" && String(n.title).includes("打刻")));
      const appsFromAdmin = list.filter(n => APPLICATION_NOTICE_TITLES_FROM_ADMIN.has(n.title));
      setUnreadCount(list.filter(n => !n.title.includes("打刻") && !APPLICATION_NOTICE_TITLES_FROM_ADMIN.has(n.title)).length);
      setAppNoticeCount(appsFromAdmin.length);
      setApplicationNotices(appsFromAdmin);

      //アンケート回答依頼
      const anketoNotifList = list.filter(n => n.category === "event_ended_anketo");
      setAnketoNotices(anketoNotifList);

      // 打刻忘れ直接チェック（通知に依存せず本日のシフト×打刻状況を確認）
      const punchData = punchRes.status === "fulfilled" ? punchRes.value.data : null;
      const hasForgotPunch = punchData?.hasForgotPunch === true;

      if (hasForgotPunch) {
        // APIチェックで打刻忘れ確認 → バッジを1以上にする
        const combinedCount = Math.max(notifReminders.length, 1);
        setPunchReminderCount(combinedCount);
        // 通知ベースの既存メッセージがあればそちら、なければ直接チェック結果でメッセージ作成
        if (notifReminders.length > 0) {
          setPunchReminderNotices(notifReminders);
        } else {
          const ss = punchData.scheduleStart || "";
          const se = punchData.scheduleEnd || "";
          const schedMsg = ss || se ? `（シフト: ${ss}〜${se}）` : "";
          setPunchReminderNotices([{
            id: "__punch_check__",
            title: PUNCH_REMINDER_TITLE,
            message: `本日の打刻が未記録です。${schedMsg}お手数ですが打刻をお願いします。`,
            category: "personal",
          }]);
        }
      } else {
        // 打刻忘れなし → バッジ・ダミー通知を消す（①を出さない）
        setPunchReminderCount(0);
        setPunchReminderNotices([]);
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchSubordinateOvertime = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/subordinate-overtime/");
      setSubordinateOvertimeList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error(e);
      }
      setSubordinateOvertimeList([]);
    }
  }, []);

  const fetchSubordinateForgotPunch = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/subordinate-forgot-punch/");
      setIsSupervisor(Boolean(res.data?.isSupervisor));
      setForgotPunchCount(res.data?.count ?? 0);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        console.error(e);
      }
    }
  }, []);

  // 申請お知らせ（却下・承認等）を既読にしてバッジ①を消す。申請画面へ遷移する際に呼ぶ。
  const markApplicationNoticesRead = useCallback(async () => {
    const ids = (applicationNotices || []).map((n) => n.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      await Promise.allSettled(ids.map((id) => api.post(API.NOTIFICATION_MARK_READ(id))));
    } catch (e) {
      console.error("Failed to mark application notices read:", e);
    }
    // バッジを即時クリアし、サーバ状態も再同期
    setApplicationNotices([]);
    setAppNoticeCount(0);
    fetchNotifications();
    refreshStarArea();
  }, [applicationNotices, fetchNotifications, refreshStarArea]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!employee) return;
    const memberId = employee.memberId || employee.employeeId;
    api.get(API.LEAVE(memberId)).then(res => {
      setRemainingLeave(res.data?.remainingLeaveDays ?? 0);
    }).catch(() => setRemainingLeave(0)).finally(() => setLeaveLoading(false));
    
    fetchNotifications();
    fetchSubordinateOvertime();
    fetchSubordinateForgotPunch();
    const timer = setInterval(() => {
      fetchNotifications();
      fetchSubordinateOvertime();
      fetchSubordinateForgotPunch();
    }, 30000);
    return () => clearInterval(timer);
  }, [employee, fetchNotifications, fetchSubordinateOvertime, fetchSubordinateForgotPunch]);

  useEffect(() => {
    const onWorktimeUpdated = () => {
      fetchNotifications();
      fetchWorkStatus();
    };
    window.addEventListener("worktime-punch-state-changed", onWorktimeUpdated);
    return () => window.removeEventListener("worktime-punch-state-changed", onWorktimeUpdated);
  }, [fetchNotifications, fetchWorkStatus]);

  // --- QR Code Token ---
  const fetchQrToken = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/qr-token/");
      setQrPayload(JSON.stringify(res.data));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchQrToken();
    const qrTimer = setInterval(fetchQrToken, 25 * 60 * 1000);
    return () => clearInterval(qrTimer);
  }, [fetchQrToken]);

  // --- Event Handlers ---
  const handleLogout = () => { if (window.confirm('ログアウトしますか？')) { logout(); navigate(ROUTES.LOGIN); }};
  const goToWorktime = () => { setShowPunchReminderModal(false); navigate(ROUTES.WORKTIME); };

  // 下部固定のお知らせバーの高さを測り、その分だけ本文の下余白を確保する
  // （バーに本文の末尾が隠れないようにする。アイコンの折り返しで高さが変わるため実測する）
  const [notifBarHeight, setNotifBarHeight] = useState(0);
  const notifBarObserverRef = useRef(null);
  const notifBarRef = useCallback((el) => {
    if (notifBarObserverRef.current) {
      notifBarObserverRef.current.disconnect();
      notifBarObserverRef.current = null;
    }
    if (!el) {
      setNotifBarHeight(0);
      return;
    }
    setNotifBarHeight(el.offsetHeight);
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => setNotifBarHeight(el.offsetHeight));
      ro.observe(el);
      notifBarObserverRef.current = ro;
    }
  }, []);

  if (loading) return <div className="employee-card-loading">Loading...</div>;
  if (!employee) {
    // 社員情報が取れない（ログイン期限切れ等）はログイン画面へ誘導
    navigate(ROUTES.LOGIN, { replace: true, state: { message: "ログインしてください（セッションが切れました）。" } });
    return null;
  }

  const keyId = employee.memberId || employee.employeeId || '';
  const displayField = (value, canShow) => (canShow ? (value || "-") : "xxxxx");

  // 複数行テキストの空行を詰める（連続改行を1つに）。改行自体は保持。
  const compactLines = (s) =>
    (s || "").replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n{2,}/g, "\n").trim();

  // 社員証ダブルタップで裏面へ
  const handleDoubleTouch = () => {
    navigate("/card_back");
  };

  return (
    <div onDoubleClick={handleDoubleTouch}>
    <div
      className="employee-card-wrapper"
      style={notifBarHeight ? { paddingBottom: notifBarHeight + 16 } : undefined}
    >
      <div id="employee-card-container">
        {/* Header */}
        <div id="topBar">
          <div className="icon-box-small">
            {companyInfo?.logo && settings?.show_company_logo && (
              <img src={companyInfo.logo} alt="logo" style={{ height: 30 }} />
            )}
          </div>
          <div id="title">社員証</div>
          <div className="issue-date">
            <span id="titleLabel">交付日</span><br />
            <span id="titleDate">{employee.issueDate ?? ''}</span>
          </div>
        </div>

        {/* --- Info View (Photo + Name + QR) --- */}
        <div id="infoViewMain">
          <div className="employee-photo">
            {settings?.show_photo ? (
              // 写真は認証付きの配信 API から取得する（/media/ の直リンクは使わない。docs/spec_file_access.md）
              <AuthImage url={keyId ? `/api/files/employees/${encodeURIComponent(keyId)}/photo/` : null} alt="photo"
                fallback={<div className="photo-placeholder" />} />
            ) : <div className="photo-placeholder">xxxxx</div>}
          </div>
          
          <div className="info-and-qr">
            <div className="info-text">
              <p><strong>社員番号:</strong> {keyId}</p>
              <p><strong>氏名:</strong> {displayField(employee.name, settings?.show_name)}</p>
            </div>
            
            <div className="qr-code-container">
              {qrPayload ? (
                <QRCodeSVG value={qrPayload} size={70} level="M" bgColor="#ffffff" />
              ) : <div className="qr-placeholder-small">QR</div>}
            </div>
          </div>
        </div>

      </div>

      {/* --- 機能アイコン --- */}
      
      <div className="employee-container">
        <div style={{ marginTop: 16, background: "#eff6ff", padding: "10px 12px", borderRadius: 6, border: "1px solid #bfdbfe" }}>
          <div style={{ fontWeight: 700, color: "#1e40af", marginBottom: 6, fontSize: "0.88rem" }}>
            経営理念
          </div>
          <div id="point" style={{ whiteSpace: "pre-line" }}>{displayField(compactLines(companyInfo.description), settings.show_company_intro)}</div>
        </div>

        {/* 今日の一言（本日分がある時だけ表示） */}
        {todayQuote ? (
          <div style={{ marginTop: 12, background: "#fffbeb", padding: "10px 12px", borderRadius: 6, border: "1px solid #fcd34d" }}>
            <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6, fontSize: "0.88rem" }}>
              今日の一言
            </div>
            <div style={{ whiteSpace: "pre-line", color: "#78350f" }}>{todayQuote}</div>
          </div>
        ) : null}

        {/* 打刻ボタン */}
        {renderPunchButton()}

        {/* 上司向け: 部下残業申請・部下打刻忘れの各画面へ。
            該当が発生したときだけ、件数付きで表示する（0件のボタンは出さない）。 */}
        {isSupervisor && (subordinateOvertimeList.length > 0 || forgotPunchCount > 0) && (
          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {subordinateOvertimeList.length > 0 && (
              <button
                type="button"
                onClick={() => navigate('/supervisor/overtime')}
                style={{ flex: "1 1 0", minWidth: 150, padding: "12px 14px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, cursor: "pointer", fontWeight: 700, color: "#92400e", fontSize: "0.9rem" }}
              >
                部下残業申請{subordinateOvertimeList.length > 0 ? `（${subordinateOvertimeList.length}）` : ""}
              </button>
            )}
            {forgotPunchCount > 0 && (
              <button
                type="button"
                onClick={() => navigate('/supervisor/forgot-punch')}
                style={{ flex: "1 1 0", minWidth: 150, padding: "12px 14px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, cursor: "pointer", fontWeight: 700, color: "#991b1b", fontSize: "0.9rem" }}
              >
                部下打刻忘れ{forgotPunchCount > 0 ? `（${forgotPunchCount}）` : ""}
              </button>
            )}
          </div>
        )}
        {/*}
        <div className="employee-back-header">
          <button onClick={handleLogout} className="employee-back-logout">ログアウト</button>
        </div>

        <div className="icon-grid four-cols"> 
          {/* 打刻 */}
          {/*}
          <div className="icon-box worktime" onClick={() => punchReminderCount > 0 ? setShowPunchReminderModal(true) : navigate(ROUTES.WORKTIME)}>
            <img src={`${process.env.PUBLIC_URL}/images/icon-time.png`} alt="Time" />
            {punchReminderCount > 0 && <span className="punch-reminder-badge">{numberToCircle(punchReminderCount)}</span>}
            <div className="icon-box-text">打刻</div>
          </div>

          <Link to="/emergency_contact" className="icon-box emergency">
            <img src={`${process.env.PUBLIC_URL}/images/icon-phone.png`} alt="Emergency" />
            <div className="icon-box-text">緊急連絡</div>
          </Link>

          {/* イベント */}
          {/*}
          <Link to="/events" className="icon-box event">
            <img src={`${process.env.PUBLIC_URL}/images/icon-event.png`} alt="Event" />
            <div className="icon-box-text">イベント</div>
          </Link>

          {/* 申請 */}
          {/*}
          <Link to={ROUTES.APPLICATIONS} className="icon-box applications">
            <img src={`${process.env.PUBLIC_URL}/images/icon-form.png`} alt="App" />
            {appNoticeCount > 0 && <span className="application-notice-badge">{numberToCircle(appNoticeCount)}</span>}
            <div className="icon-box-text">申請</div>
          </Link>

          {useEmployeeSchedule && (
            <Link to="/schedule" className="icon-box schedule">
              <img src={`${process.env.PUBLIC_URL}/images/icon-calendar.svg`} alt="Schedule" />
              <div className="icon-box-text">スケジュール</div>
            </Link>
          )}

          <Link to="/salary" className="icon-box salary">
            <img src={`${process.env.PUBLIC_URL}/images/icon-pay.png`} alt="Salary" />
            <div className="icon-box-text">給与明細</div>
          </Link>

          {/* 日付 (表示のみ) */}
          {/*}
          <div className="icon-box vacation icon-box-info-only">
            <div className="leave-number">{leaveLoading ? '...' : numberToCircle(remainingLeave)}</div>
            <div className="icon-box-text">有給残</div>
          </div>

          {/* 通知 */}
          {/*}
          <Link to="/notice" className="icon-box notice">
            <img src={`${process.env.PUBLIC_URL}/images/icon-notice.png`} alt="Notice" />
            {unreadCount > 0 && <span className="notice-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            <div className="icon-box-text">お知らせ</div>
          </Link>
        </div>
        */}
        
      
      {/* --- ⭐エリア：申請一時保存 + admin お知らせ --- */}{/*}
      {((showDraftInStar && applicationDraftLines.length > 0) || displayNotifications.length > 0 || pendingAnketo.length > 0 || pendingParticipation.length > 0 || punchReminderCount > 0) && (
        <div style={{ marginTop: 16, background: "#eff6ff", padding: "10px 12px", borderRadius: 6, border: "1px solid #bfdbfe" }}>
          {/*}
          <div style={{ fontWeight: 700, color: "#1e40af", marginBottom: 6, fontSize: "0.88rem" }}>
            ⭐ お知らせ
          </div>
          
          {punchReminderCount > 0 && punchReminderNotices.length > 0 && (
            <div
              style={{
                marginBottom: 10,
                paddingBottom: 8,
                borderBottom: "1px dashed #93c5fd",
              }}
            >
              {punchReminderNotices.map((n, i) => (
                <div
                  key={n.id || i}
                  style={{
                    padding: "6px 8px",
                    background: "#fff7ed",
                    borderRadius: 5,
                    border: "1px solid #fdba74",
                    marginBottom: 4,
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#c2410c" }}>
                    🔔 {n.title || "打刻のお願い"}
                  </div>
                  <div style={{ fontWeight: 500, fontSize: "0.78rem", color: "#92400e", marginTop: 2, lineHeight: 1.4 }}>
                    {n.content || n.message}
                  </div>
                </div>
              ))}
            </div>
          )}
          {showDraftInStar && applicationDraftLines.length > 0 && (
            <div
              className="employee-card-draft-strip"
              style={{
                marginBottom: 10,
                paddingBottom: 8,
                borderBottom: "1px dashed #93c5fd",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "0.8rem", color: "#92400e", marginBottom: 4 }}>
                申請一時保存
              </div>
              {applicationDraftLines.map((line) => (
                <div key={line} style={{ fontSize: "0.78rem", color: "#78350f", fontWeight: 600, marginBottom: 2 }}>
                  {line}
                </div>
              ))}
            </div>
          )}
          {pendingAnketo.length > 0 && (
            <div
              style={{
                marginBottom: 10,
                paddingBottom: 8,
                borderBottom: "1px dashed #93c5fd",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "0.8rem", color: "#5b21b6", marginBottom: 6 }}>
                アンケート回答のお願い
              </div>
              {pendingAnketo.map((ev) => (
                <div
                  key={ev.id}
                  style={{
                    marginBottom: 8,
                    padding: "8px 6px",
                    background: "#f5f3ff",
                    borderRadius: 6,
                    border: "1px solid #c4b5fd",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "0.83rem", color: "#4c1d95" }}>
                    {ev.eventName || ev.title || "（イベント名なし）"}
                  </div>
                  {ev.eventDate && (
                    <div style={{ fontSize: "0.75rem", color: "#7c3aed", marginTop: 2 }}>
                      {ev.eventDate}
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <Link
                      to={`/event/${ev.id}/anketo`}
                      style={{
                        display: "inline-block",
                        background: "#7c3aed",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      アンケートに回答する
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pendingParticipation.length > 0 && (
            <div
              style={{
                marginBottom: 10,
                paddingBottom: 8,
                borderBottom: "1px dashed #93c5fd",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "0.8rem", color: "#0f766e", marginBottom: 6 }}>
                イベント参加回答のお願い
              </div>
              {pendingParticipation.map((ev) => (
                <div
                  key={ev.id}
                  style={{
                    marginBottom: 8,
                    padding: "8px 6px",
                    background: "#ecfeff",
                    borderRadius: 6,
                    border: "1px solid #99f6e4",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "0.87rem", color: "#115e59", marginBottom: 2 }}>
                    📅 {ev.eventName || ev.event_name || ev.title || "（イベント名なし）"}
                  </div>
                  {ev.eventDate && (
                    <div style={{ fontSize: "0.75rem", color: "#0f766e", marginBottom: 4 }}>
                      {ev.eventDate}
                    </div>
                  )}
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: "0.78rem",
                      color: "#0f766e",
                      marginTop: 2,
                      lineHeight: 1.35,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {ev.message}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Link
                      to={`/event/${ev.id}`}
                      style={{
                        display: "inline-block",
                        background: "#0f766e",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      参加可否を送信する
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
          {displayNotifications.map((n) => {
            const dateTimeStr = n.created_at
              ? (() => {
                  const d = new Date(n.created_at);
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  const h = String(d.getHours()).padStart(2, "0");
                  const min = String(d.getMinutes()).padStart(2, "0");
                  return `${y}/${m}/${day} ${h}:${min}`;
                })()
              : "";
            return (
              <div
                key={n.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  borderBottom: "1px solid #dbeafe",
                  paddingBottom: 5,
                  marginBottom: 5,
                  ...(n.title === SCHEDULE_REMINDER_TITLE
                    ? { background: "#fffbeb", borderRadius: 6, padding: "8px 6px", border: "1px solid #fcd34d" }
                    : {}),
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "0.83rem", color: "#1e3a5f", flexGrow: 1 }}>
                  {n.title}
                  {dateTimeStr &&
                    !String(n.title || "").startsWith(SUBORDINATE_APPLICATION_CONFIRM_TITLE_PREFIX) && (
                    <span style={{ fontWeight: 500, fontSize: "0.78rem", color: "#64748b", marginLeft: 6 }}>
                      {dateTimeStr}
                    </span>
                  )}
                  {n.title === SCHEDULE_REMINDER_TITLE && (n.message || n.content) && (
                    <div style={{ fontWeight: 500, fontSize: "0.78rem", color: "#78350f", marginTop: 4, lineHeight: 1.35 }}>
                      {n.message || n.content}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleMarkAsRead(n.id)}
                  style={{ background: "#93c5fd", color: "#1e3a5f", border: "none", padding: "2px 7px", borderRadius: 4, fontSize: "0.72rem", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  既読
                </button>
              </div>
            );
          })}
          */}
          {(punchReminderCount > 0 ||
            applicationDraftLines.length > 0 ||
            displayNotifications.length > 0 ||
            pendingAnketo.length > 0 ||
            pendingParticipation.length > 0 ||
            eventCancelNotices.length > 0 ||
            appNoticeCount > 0) && (
            <div className="fixed-notification-bar" ref={notifBarRef}>
              <div
                className="notification-icons-grid"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "20px",
                  justifyContent: "center",
                  padding: "15px",
                  background: "#f1f5f9",
                  borderRadius: "12px",
                  marginTop: "10px",
                }}
              >
                {punchReminderCount > 0 && (
                  <div
                    className="icon-badge-wrapper"
                    onClick={() => setShowPunchReminderModal(true)}
                    style={{ cursor: "pointer", textAlign: "center" }}
                  >
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icon-time.png`}
                        alt="打刻のお願い"
                        style={{ width: 60, height: 60, objectFit: "contain", display: "block" }}
                      />
                      <span style={{ position: "absolute", top: "-12px", right: "-10px", fontSize: "1.2rem", color: "#ef4444" }}>
                        {numberToCircle(punchReminderCount)}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.7rem", fontWeight: "bold", color: "#9a3412" }}>打刻</div>
                  </div>
                )}
            {appNoticeCount > 0 && (
              <div
                className="icon-badge-wrapper"
                onClick={() => setShowApplicationModal(true)}
                style={{ cursor: 'pointer', textAlign: 'center' }}
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-form.png`}
                    alt="申請お知らせ"
                    style={{ width: 60, height: 60, objectFit: 'contain', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', top: '-12px', right: '-10px', fontSize: '1.2rem', color: '#ef4444' }}>
                    {numberToCircle(appNoticeCount)}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#b45309' }}>申請</div>
              </div>
            )}

            {applicationDraftLines.length > 0 && (
              <div className="icon-badge-wrapper" onClick={() => navigate('/applications')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-form.png`}
                    alt="Application"
                    style={{ width: 60, height: 60, objectFit: 'contain', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', top: '-12px', right: '-10px', fontSize: '1.2rem', color:'#ef4444' }}>
                    {numberToCircle(applicationDraftLines.length)}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#854d0e' }}>一時保存</div>
              </div>
            )}

            {(pendingParticipation.length > 0 || pendingAnketo.length > 0 || eventCancelNotices.length > 0 || anketoNotices.length > 0) && (
              <div className="icon-badge-wrapper"
               onClick={() => {
                if (anketoNotices.length > 0 || pendingAnketo.length > 0) {
                  setShowAnketoModal(true);
                } else if (autoEventReminders.length > 0) {
                  setSelectedEventNotice(autoEventReminders[0]);
                  setShowEventModal(true);
                } else {
                  navigate('/events')
                }
               }}
                 style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-event.png`}
                    alt="Event"
                    style={{ width: 60, height: 60, objectFit: 'contain', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', top: '-12px', right: '-10px', fontSize: '1.2rem', color:'#ef4444' }}>
                    {numberToCircle(pendingParticipation.length + pendingAnketo.length + eventCancelNotices.length)}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#115e59' }}>イベント</div>
              </div>
            )}

            {displayNotifications.length > 0 && (
              <div className="icon-badge-wrapper" 
                onClick={() => {
                  if (deadlineReachedNotices.length > 0){
                    setSelectedNoticeDeadline(deadlineReachedNotices[0]);
                    setShowNoticeDeadlineModal(true);
                  } else {
                    navigate('/notice')
                  }
                }} 
                style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-notice.png`}
                    alt="Notice"
                    style={{ width: 60, height: 60, objectFit: 'contain', display: 'block' }}
                  />
                  <span style={{ position: 'absolute', top: '-12px', right: '-10px', fontSize: '1.2rem', color:'#ef4444' }}>
                    {numberToCircle(displayNotifications.length)}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#1e3a5f' }}>お知らせ</div>
              </div>
            )}
              </div>
            </div>
          )}
        </div>
       
      </div>

      {/* --- 申請お知らせモーダル --- */}
      {showApplicationModal && applicationNotices.length > 0 && (
        <div
          className="application-notice-overlay"
          role="dialog"
          aria-labelledby="app-notice-modal-title-card"
          aria-modal="true"
          onClick={() => setShowApplicationModal(false)}
        >
          <div className="application-notice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="app-notice-modal-title-card" className="application-notice-modal-title">申請のお知らせ</h3>
            <div className="application-notice-modal-body">
              {applicationNotices.map((notice, idx) => (
                <div key={notice.id || idx} className="application-notice-item">
                  <strong className="application-notice-item-title">{notice.title}</strong>
                  <p className="application-notice-item-content">{notice.content || notice.message || ''}</p>
                </div>
              ))}
            </div>
            <div className="application-notice-modal-actions">
              <button
                type="button"
                className="application-notice-btn-close"
                onClick={() => setShowApplicationModal(false)}
              >
                閉じる
              </button>
              <button
                type="button"
                className="application-notice-btn-go"
                onClick={() => { markApplicationNoticesRead(); setShowApplicationModal(false); navigate(ROUTES.APPLICATIONS); }}
              >
                申請画面へ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- 打刻忘れお知らせ：アイコン①クリックでメッセージ --- */}
      {/* 残業として退勤：残業終了時刻・休憩・（予定とズレた場合の）理由を入力して確定する */}
      {overtimeDialog && (
        <div className="punch-reminder-overlay" role="dialog" aria-modal="true" aria-labelledby="ot-modal-title">
          <div className="punch-reminder-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="ot-modal-title" className="punch-reminder-modal-title">残業の登録</h3>
            <div className="punch-reminder-modal-body" style={{ textAlign: "left" }}>
              {overtimeDialog.plannedEnd && (
                <p style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#475569" }}>
                  残業終了予定：<strong>{overtimeDialog.plannedEnd}</strong>
                </p>
              )}

              <label style={{ display: "block", fontWeight: 700, fontSize: "0.85rem", marginBottom: 4 }}>
                残業は何時に終わりましたか？
              </label>
              <input
                type="time"
                value={otFinishedAt}
                onChange={(e) => setOtFinishedAt(e.target.value)}
                style={{ width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, marginBottom: 12 }}
              />

              <label style={{ display: "block", fontWeight: 700, fontSize: "0.85rem", marginBottom: 4 }}>
                休憩時間
              </label>
              <select
                value={otBreakMinutes}
                onChange={(e) => setOtBreakMinutes(Number(e.target.value))}
                style={{ width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, marginBottom: 12 }}
              >
                {OVERTIME_BREAK_HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h * 60}>{h}</option>
                ))}
              </select>

              {/* 予定とちょうど同じなら理由は不要なので出さない */}
              {overtimeDialog.plannedEnd && otFinishedAt && otFinishedAt !== overtimeDialog.plannedEnd && (
                <>
                  <label style={{ display: "block", fontWeight: 700, fontSize: "0.85rem", marginBottom: 4, color: "#b45309" }}>
                    {otFinishedAt > overtimeDialog.plannedEnd ? "超過理由" : "早く終了した理由"}
                    <span style={{ color: "#dc2626" }}> *</span>
                  </label>
                  <textarea
                    value={otReason}
                    onChange={(e) => setOtReason(e.target.value)}
                    rows={3}
                    placeholder={
                      otFinishedAt > overtimeDialog.plannedEnd
                        ? "予定を超えて残業した理由を入力してください"
                        : "予定より早く残業を終えた理由を入力してください"
                    }
                    style={{ width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit" }}
                  />
                </>
              )}
            </div>
            <div className="punch-reminder-modal-actions">
              <button type="button" onClick={() => setOvertimeDialog(null)}>キャンセル</button>
              <button type="button" onClick={submitOvertimeClockOut}>退勤する</button>
            </div>
          </div>
        </div>
      )}

      {showPunchReminderModal && punchReminderNotices.length > 0 && (
        <div className="punch-reminder-overlay" role="dialog" aria-modal="true" aria-labelledby="punch-reminder-modal-title" onClick={() => setShowPunchReminderModal(false)}>
          <div className="punch-reminder-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="punch-reminder-modal-title" className="punch-reminder-modal-title">打刻のお願い</h3>
            <div className="punch-reminder-modal-body">
              {punchReminderNotices.map((n, i) => (
                <div key={n.id || i} className="punch-reminder-item">
                  <strong className="punch-reminder-item-title">{n.title || PUNCH_REMINDER_TITLE}</strong>
                  <p className="punch-reminder-item-content">{n.content || n.message}</p>
                </div>
              ))}
            </div>
            <div className="punch-reminder-modal-actions">
              <button type="button" className="punch-reminder-btn-close" onClick={() => setShowPunchReminderModal(false)}>閉じる</button>
              <button type="button" className="punch-reminder-btn-go" onClick={goToWorktime}>打刻・実績修正へ</button>
            </div>
          </div>
        </div>
      )}
      
      {/* ---イベントリマインド ---*/}
      {showEventModal && selectedEventNotice && (
        <div 
          className="application-notice-overlay" 
          role="dialog" 
          aria-modal="true" 
          onClick={() => setShowEventModal(false)}
        >
          <div className="application-notice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="application-notice-modal-title" style={{ color: "#ef4444" }}>⚠️ 回答締切のお知らせ</h3>
            <div className="application-notice-modal-body" style={{ textAlign: "center", padding: "15px 0" }}>
              <p style={{ fontSize: "1rem", fontWeight: "700", color: "#115e59", lineHeight: 1.5 }}>
                「{selectedEventNotice.eventName}」<br />
                へのお返事お願いします。
              </p>
              <p style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "8px" }}>
                ※回答締切日になりました。参加可否の選択をお願いします。
              </p>
            </div>
            <div className="application-notice-modal-actions">
              <button 
                type="button" 
                className="application-notice-btn-close" 
                onClick={() => setShowEventModal(false)}
              >
                閉じる
              </button>
              <button 
                type="button" 
                className="application-notice-btn-go" 
                style={{ backgroundColor: "#115e59" }}
                onClick={() => {
                  setShowEventModal(false);
                  // イベント詳細へ遷移。アンケートも参加可否も同じ画面で行う想定なので、どちらの通知でも同じ遷移先。
                  navigate(`/event/${selectedEventNotice.id}`);
                }}
              >
                回答する
              </button>
            </div>
          </div>
        </div>
      )}
       {/* --- お知らせ期限切れ ---*/}
      {showNoticeDeadlineModal && selectedNoticeDeadline && (
        <div 
          className="application-notice-overlay" 
          role="dialog" 
          aria-modal="true" 
          onClick={() => setShowNoticeDeadlineModal(false)}
        >
          <div className="application-notice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="application-notice-modal-title" style={{ color: "#dc2626" }}>⚠️ 確認期限のお知らせ</h3>
            <div className="application-notice-modal-body" style={{ textAlign: "center", padding: "15px 0" }}>
              <p style={{ fontSize: "1rem", fontWeight: "700", color: "#1e3a5f", lineHeight: 1.5 }}>
                「{selectedNoticeDeadline.title}」<br />
                の確認期限が過ぎています。<br />
                至急内容のご確認をお願いします。
              </p>
            </div>
            <div className="application-notice-modal-actions">
              <button 
                type="button" 
                className="application-notice-btn-close" 
                onClick={() => setShowNoticeDeadlineModal(false)}
              >
                閉じる
              </button>
              <button 
                type="button" 
                className="application-notice-btn-go" 
                style={{ backgroundColor: "#1e3a5f" }}
                onClick={() => {
                  setShowNoticeDeadlineModal(false);
                  // 通知一覧ページに遷移
                  navigate('/notice');
                }}
              >
                確認する
              </button>
            </div>
          </div>
        </div>
      )}
        {/* --- アンケート通知 ---*/}
      {showAnketoModal && (anketoNotices.length > 0 || pendingAnketo.length > 0) && (
        <div
          className="application-notice-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowAnketoModal(false)}
        >
          <div className="application-notice-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="application-notice-modal-title" style={{ color: "#115e59" }}>📝 アンケート回答のお願い</h3>
            <div className="application-notice-modal-body">
              {anketoNotices.map((notice, idx) => (
                <div key={`notice-${notice.id || idx}`} className="application-notice-item" style={{ borderBottom: "1px dashed #ddd", paddingBottom: "12px", marginBottom: "12px" }}>
                  <strong className="application-notice-item-title" style={{ color: "#115e59", fontSize: "0.95rem" }}>{notice.title}</strong>
                  <p className="application-notice-item-content" style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", marginTop: "8px", color: "#334155", lineHeight: 1.5 }}>
                    {notice.content || notice.message || ''}
                  </p>
                  <div style={{ marginTop: "12px", textAlign: "right" }}>
                    <button
                      type="button"
                      className="application-notice-btn-go"
                      style={{ backgroundColor: "#115e59", color: "#fff", padding: "6px 14px", borderRadius: "4px", fontSize: "0.8rem", cursor: "pointer", border: "none", fontWeight: "bold" }}
                      onClick={async () => {
                        try {
                          // 1. 通知を既読にするAPIを叩く
                          await api.post(`/api/notifications/${notice.id}/mark-read/`);
                          setShowAnketoModal(false);

                          // 2. アンケートページへ遷移（event_idが通知に含まれている前提）
                          if (notice.event_id) {
                            navigate(`/event/${notice.event_id}/anketo`);
                          }
                        } catch (e) {
                          console.error("通知を既読にするか遷移する際にエラーが発生しました:", e);
                        }
                      }}
                    >
                      回答画面へ進む
                    </button>
                  </div>
                </div>
              ))}
              {pendingAnketo
                .filter((ev) => !anketoNotices.some((n) => String(n.event_id) === String(ev.id)))
                .map((ev, idx) => (
                <div key={`pending-${ev.id || idx}`} className="application-notice-item" style={{ borderBottom: "1px dashed #ddd", paddingBottom: "12px", marginBottom: "12px" }}>
                  <strong className="application-notice-item-title" style={{ color: "#115e59", fontSize: "0.95rem" }}>
                    【アンケート依頼】{ev.eventName || ev.title || "（イベント名なし）"}
                  </strong>
                  <p className="application-notice-item-content" style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", marginTop: "8px", color: "#334155", lineHeight: 1.5 }}>
                    {`イベント「${ev.eventName || ev.title || ""}」は無事に終了いたしました。\nご参加いただきありがとうございました。\n\nお手数ですが、今後の改善のためにアンケートへの回答にご協力をお願いいたします。`}
                  </p>
                  {ev.eventDate && (
                    <p style={{ fontSize: "0.75rem", color: "#7c3aed", marginTop: "4px" }}>
                      開催日: {ev.eventDate}
                    </p>
                  )}
                  <div style={{ marginTop: "12px", textAlign: "right" }}>
                    <button
                      type="button"
                      className="application-notice-btn-go"
                      style={{ backgroundColor: "#115e59", color: "#fff", padding: "6px 14px", borderRadius: "4px", fontSize: "0.8rem", cursor: "pointer", border: "none", fontWeight: "bold" }}
                      onClick={() => {
                        setShowAnketoModal(false);
                        navigate(`/event/${ev.id}/anketo`);
                      }}
                    >
                      回答画面へ進む
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="application-notice-modal-actions">
              <button
                type="button"
                className="application-notice-btn-close"
                onClick={() => setShowAnketoModal(false)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeCard;